import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreVertical, Plus } from 'lucide-react';
import {
  REPAIR_CATEGORIES,
  bulkSetRepairServiceScope,
  createRepairService,
  formatServiceScope,
  listRepairServices,
  listScopeSuggestions,
  updateRepairService,
  type RepairService,
  type ServiceScopeType,
} from '@/lib/api/repair-services.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function priceRangeLabel(service: RepairService) {
  if (service.scopeType && service.scopeType !== 'per_model' && service.flatPrice != null) {
    return formatCurrency(service.flatPrice);
  }
  if (service.modelCount && service.modelCount > 0 && service.priceMin != null && service.priceMax != null) {
    if (service.priceMin === service.priceMax) return formatCurrency(service.priceMin);
    return `${formatCurrency(service.priceMin)} – ${formatCurrency(service.priceMax)}`;
  }
  if (service.defaultPrice && Number(service.defaultPrice) > 0) {
    return formatCurrency(service.defaultPrice);
  }
  return null;
}

export function RepairServicesListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState('');
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ServiceScopeType | ''>('');
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('screen');
  const [newPrice, setNewPrice] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkScope, setBulkScope] = useState<ServiceScopeType>('all_devices');
  const [bulkPrice, setBulkPrice] = useState('');

  const servicesQuery = useQuery({
    queryKey: ['repair-services-catalog', category, makeId, modelId, search, activeOnly, scopeFilter],
    queryFn: () =>
      listRepairServices({
        categorySlug: category || undefined,
        makeId: makeId || undefined,
        modelId: modelId || undefined,
        search: search || undefined,
        active: activeOnly ? true : undefined,
        includeInactive: !activeOnly,
        scopeType: scopeFilter || undefined,
      }),
  });

  const suggestionsQuery = useQuery({
    queryKey: ['repair-scope-suggestions'],
    queryFn: listScopeSuggestions,
  });

  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: listDeviceMakes });
  const modelsQuery = useQuery({
    queryKey: ['device-models', makeId],
    queryFn: () => listDeviceModels(makeId ? { makeId } : undefined),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createRepairService({
        name: newName.trim(),
        categorySlug: newCategory,
        defaultPrice: newPrice || '0',
      }),
    onSuccess: (service) => {
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      setCreateOpen(false);
      setNewName('');
      setNewPrice('');
      toastSuccess('Service created! Now set scope or model pricing.');
      navigate(`/settings/repair-services/${service.id}`);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      updateRepairService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      toastSuccess('Service updated');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const bulkScopeMutation = useMutation({
    mutationFn: () =>
      bulkSetRepairServiceScope({
        serviceIds: selectedIds,
        scopeType: bulkScope,
        flatPrice: bulkScope === 'per_model' ? null : Number(bulkPrice),
        deactivatePricingRows: bulkScope !== 'per_model',
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['repair-scope-suggestions'] });
      setSelectedIds([]);
      setBulkOpen(false);
      toastSuccess(`Updated scope on ${result.count} services`);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const applySuggestionMutation = useMutation({
    mutationFn: (s: { serviceId: string; category: string; flatPrice: string }) =>
      bulkSetRepairServiceScope({
        serviceIds: [s.serviceId],
        scopeType: 'categories',
        scopeCategories: [s.category as 'phone'],
        flatPrice: Number(s.flatPrice),
        deactivatePricingRows: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['repair-scope-suggestions'] });
      toastSuccess('Converted to category scope');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const filteredModels = useMemo(() => {
    if (!makeId) return modelsQuery.data ?? [];
    return (modelsQuery.data ?? []).filter((m) => m.makeId === makeId);
  }, [makeId, modelsQuery.data]);

  function toggleSelect(id: string) {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">Repair Services</h1>
          <p className="text-sm text-slate-500">Manage your repair catalog, scopes, and per-device pricing.</p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4" />
          New Service
        </button>
      </div>

      {(suggestionsQuery.data?.length ?? 0) > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm">
          <p className="font-semibold text-amber-950">Suggested scope conversions</p>
          <p className="mt-1 text-amber-900/80">
            These per-model services have the same price across many models in a category.
          </p>
          <ul className="mt-3 space-y-2">
            {(suggestionsQuery.data ?? []).slice(0, 5).map((s) => (
              <li key={`${s.serviceId}-${s.category}`} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <span className="font-medium">{s.serviceName}</span>
                  {' → '}
                  {s.category} · {s.modelCount} models · {formatCurrency(s.flatPrice)}
                </span>
                <button
                  type="button"
                  className="font-semibold text-primary hover:underline"
                  disabled={applySuggestionMutation.isPending}
                  onClick={() => applySuggestionMutation.mutate(s)}
                >
                  Convert
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {selectedIds.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
          <span className="font-semibold text-slate-900">{selectedIds.length} selected</span>
          <button
            type="button"
            className="rounded-lg bg-primary px-3 py-1.5 font-semibold text-white"
            onClick={() => setBulkOpen(true)}
          >
            Set scope…
          </button>
          <button type="button" className="text-slate-600 hover:underline" onClick={() => setSelectedIds([])}>
            Clear
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {REPAIR_CATEGORIES.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as ServiceScopeType | '')}
        >
          <option value="">All scopes</option>
          <option value="per_model">Per model</option>
          <option value="all_devices">All devices</option>
          <option value="categories">Device types</option>
          <option value="makes">Manufacturers</option>
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={makeId}
          onChange={(e) => { setMakeId(e.target.value); setModelId(''); }}
        >
          <option value="">All Brands</option>
          {(makesQuery.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          disabled={!makeId}
        >
          <option value="">All Models</option>
          {filteredModels.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <input
          className="min-w-[200px] flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm"
          placeholder="Search services…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          Active only
        </label>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-3 py-3 w-10" />
              <th className="px-4 py-3">Service Name</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Scope</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">POS</th>
              <th className="px-4 py-3">Portal</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(servicesQuery.data ?? []).map((service) => {
              const range = priceRangeLabel(service);
              return (
                <Fragment key={service.id}>
                  <tr className={service.active === false ? 'opacity-60' : ''}>
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(service.id)}
                        onChange={() => toggleSelect(service.id)}
                      />
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-950">{service.name}</td>
                    <td className="px-4 py-3">{prettyLabel(service.categorySlug ?? 'other')}</td>
                    <td className="px-4 py-3 text-slate-700">{formatServiceScope(service)}</td>
                    <td className="px-4 py-3">
                      {range ? (
                        range
                      ) : (
                        <span className="font-medium text-amber-700">$0.00 — Set prices</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{service.displayOnPos !== false ? '✓' : '—'}</td>
                    <td className="px-4 py-3">{service.displayOnPortal !== false ? '✓' : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/settings/repair-services/${service.id}`}
                          className="font-semibold text-primary hover:underline"
                        >
                          Edit
                        </Link>
                        <div className="relative">
                          <button
                            type="button"
                            className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
                            onClick={() => setMenuOpen(menuOpen === service.id ? null : service.id)}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {menuOpen === service.id ? (
                            <div className="absolute right-0 z-20 mt-1 w-48 rounded-xl border border-slate-200 bg-white py-1 text-left shadow-lg">
                              <button
                                type="button"
                                className="block w-full px-4 py-2 hover:bg-slate-50"
                                onClick={() => {
                                  setMenuOpen(null);
                                  updateMutation.mutate({
                                    id: service.id,
                                    body: { displayOnPos: !service.displayOnPos },
                                  });
                                }}
                              >
                                {service.displayOnPos !== false ? 'Hide from POS' : 'Show on POS'}
                              </button>
                              <button
                                type="button"
                                className="block w-full px-4 py-2 hover:bg-slate-50"
                                onClick={() => {
                                  setMenuOpen(null);
                                  updateMutation.mutate({
                                    id: service.id,
                                    body: { displayOnPortal: !service.displayOnPortal },
                                  });
                                }}
                              >
                                {service.displayOnPortal !== false ? 'Hide from Portal' : 'Show on Portal'}
                              </button>
                              <button
                                type="button"
                                className="block w-full px-4 py-2 text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setMenuOpen(null);
                                  updateMutation.mutate({ id: service.id, body: { active: false } });
                                }}
                              >
                                Delete Service
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {servicesQuery.isLoading ? (
          <div className="p-8 text-center text-slate-500">Loading services…</div>
        ) : null}
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-slate-950">New Repair Service</h2>
            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="font-medium">Service Name *</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Category</span>
                <select
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                >
                  {REPAIR_CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium">Default Price (fallback)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!newName.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Create Service →
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold">Bulk set scope</h2>
            <p className="mt-1 text-sm text-slate-500">
              Apply to {selectedIds.length} selected services. Pricing rows are deactivated when leaving per-model.
            </p>
            <label className="mt-4 block text-sm">
              Scope
              <select
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={bulkScope}
                onChange={(e) => setBulkScope(e.target.value as ServiceScopeType)}
              >
                <option value="all_devices">All devices</option>
                <option value="categories">Device types (use editor for categories)</option>
                <option value="makes">Manufacturers (use editor for makes)</option>
                <option value="per_model">Per model</option>
              </select>
            </label>
            {bulkScope !== 'per_model' ? (
              <label className="mt-3 block text-sm">
                Flat price *
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                />
              </label>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setBulkOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={
                  bulkScopeMutation.isPending ||
                  (bulkScope !== 'per_model' && (!bulkPrice || Number.isNaN(Number(bulkPrice))))
                }
                onClick={() => bulkScopeMutation.mutate()}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
